import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowUpRight,
  FiEdit3,
  FiMessageCircle,
  FiUser,
  FiZap,
} from "react-icons/fi";
import SiteFooter from "../components/SiteFooter";
import "./smallchains.css";

const contractAddress = "0x3104Fd9056073f9106461795FEc3cF3166695A08";
const contractUrl = `https://polygonscan.com/address/${contractAddress}`;

function SmallChains() {
  useEffect(() => {
    document.title = "How SmallChains works | Jérôme Lecoq";
  }, []);

  return (
    <main className="SmallChainsPage">
      <section className="SmallChainsHero">
        <div className="SmallChainsHeroCopy">
          <p className="SmallChainsEyebrow">On-chain publishing experiment</p>
          <h1>SmallChains</h1>
          <p className="SmallChainsLead">
            A working blog that explores what changes when a wallet is an identity,
            every post is a transferable token, and conversation is recorded through
            links between public digital objects.
          </p>
          <div className="SmallChainsActions">
            <Link className="SmallChainsPrimaryAction" to="/blog">
              Read the blog <FiArrowRight aria-hidden="true" />
            </Link>
            <a href={contractUrl} target="_blank" rel="noreferrer">
              Inspect the contract <FiArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </div>

        <figure className="SmallChainsInterface">
          <img
            src="/project-media/smallchains.png"
            alt="SmallChains blog with wallet-authenticated posts and replies"
          />
          <figcaption>
            The original interface presents the Polygon record as a familiar threaded blog.
          </figcaption>
        </figure>
      </section>

      <section className="SmallChainsDefinition" aria-labelledby="smallchains-idea">
        <p className="SmallChainsEyebrow">The idea</p>
        <div>
          <h2 id="smallchains-idea">A public protocol underneath a small blog</h2>
          <p>
            SmallChains is an experimental microblog deployed on Polygon. Publishing asks
            a connected wallet to sign a transaction. The smart contract then
            mints an ERC-721 token named <strong>SmallChains</strong>, with the symbol
            <strong> CHAIN</strong>, and emits the post text, original author, and any reply
            or attachment links into the blockchain event log. SmallChains remains an
            experiment, but the foundation for a broader social network is already in place:
            a single smart-contract setting can open publishing from any wallet. I welcome
            collaboration, particularly from people who can help navigate the legal and
            regulatory questions involved.
          </p>
        </div>
      </section>

      <section className="SmallChainsContract" aria-labelledby="smallchains-contract">
        <div className="SmallChainsSectionHeading">
          <p className="SmallChainsEyebrow">The contract</p>
          <h2 id="smallchains-contract">What exists on Polygon</h2>
        </div>

        <div className="SmallChainsMechanics">
          <article>
            <FiEdit3 aria-hidden="true" />
            <div>
              <h3>Posts are ERC-721 tokens</h3>
              <p>
                Each successful <code>mintPost</code> call creates the next numbered CHAIN
                token. The event records the text and the wallet that originally authored it.
              </p>
            </div>
          </article>
          <article>
            <FiMessageCircle aria-hidden="true" />
            <div>
              <h3>Replies and quotes are token links</h3>
              <p>
                A post can reference another NFT by contract address and token ID as either
                a reply or an attachment. The contract verifies that the referenced token exists.
              </p>
            </div>
          </article>
          <article>
            <FiZap aria-hidden="true" />
            <div>
              <h3>Edits append history</h3>
              <p>
                Editing emits a new <code>PostEdited</code> event instead of overwriting the
                original event. Only the current owner of the post token may submit an edit.
              </p>
            </div>
          </article>
          <article>
            <FiArrowUpRight aria-hidden="true" />
            <div>
              <h3>Tips go to the current owner</h3>
              <p>
                <code>tipToken</code> resolves the NFT owner and sends the attached native
                Polygon currency directly to that address, then emits a public tip event.
              </p>
            </div>
          </article>
          <article>
            <FiUser aria-hidden="true" />
            <div>
              <h3>Profiles are wallet-based</h3>
              <p>
                Status text is recorded through an event. A profile image can point to an
                NFT, but the contract first verifies that the wallet owns it.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className="SmallChainsDetails" aria-label="SmallChains technical details">
        <dl>
          <div>
            <dt>Network</dt>
            <dd>Polygon PoS</dd>
          </div>
          <div>
            <dt>Contract</dt>
            <dd>
              <a href={contractUrl} target="_blank" rel="noreferrer">
                {contractAddress.slice(0, 8)}...{contractAddress.slice(-6)}
                <FiArrowUpRight aria-hidden="true" />
              </a>
            </dd>
          </div>
          <div>
            <dt>Token</dt>
            <dd>SmallChains / CHAIN</dd>
          </div>
        </dl>
      </section>

      <SiteFooter />
    </main>
  );
}

export default SmallChains;